import AppContentView from '@nxsys/components/AppContentView';
import { AppFixedLoading, LoadingStage } from '@nxsys/components/AppFixedLoading';
import { AppVersionUpdate } from '@nxsys/components/AppVersionUpdate';
import AppEnvBanner from '@nxsys/components/AppEnvBanner';
import AppTwoFactorEnforcer from '@nxsys/components/AppTwoFactorEnforcer';
import AppOnlineStatus from '@nxsys/components/AppOnlineStatus';
import { Layouts } from '@nxsys/components/AppLayout';
import {
  useLayoutActionsContext,
  useLayoutContext,
} from '@nxsys/context/AppContextProvider/LayoutContextProvider';
import { useSidebarActionsContext } from '@nxsys/context/AppContextProvider/SidebarContextProvider';
import { filterMenuByRole } from '@nxsys/helpers/menuFilter';
import generateRoutes from '@nxsys/helpers/RouteGenerator';
import {
  buildPermissionMap,
  checkRoutePermission,
} from '@nxsys/helpers/RouteHelper';
import {
  RETURN_URL_PARAM,
  takeReturnPath,
} from '@nxsys/services/session/sessionExpiry';
import { initialUrl, getInitialUrl } from '@nxsys/constants/AppConst';
import { useAuthUser } from '@nxsys/hooks/AuthHooks';
import {
  notificationSocketOf,
  reconnectSessionSockets,
  setSessionSystem,
  socketNotification,
  socketImportProcess,
  socketOnboarding,
} from '@nxsys/services/sockets';
import { connectNotificationSocket } from '@nxsys/services/sockets/connectNotificationSocket';
import { notificationChannelOf } from '@nxsys/services/sockets/notificationChannelOf';
import { hasTenantFeed } from '@nxsys/store/slices/app/roleOf';
import { resolveAuthTarget } from '@nxsys/utils/authTarget';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import type { AppDispatch, AppState } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { notificationActions } from '@nxsys/store/slices/notification/reducer/notificationSlice';
import { messageActions } from '@nxsys/store/slices/message/reducer/messageSlice';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoutes, useLocation } from 'react-router-dom';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useVersionCheck } from '@nxsys/hooks/useVersionCheck';
import { useUrlSearchParams } from 'use-url-search-params';
import {
  getAnonymousStructure,
  authorizedStructure,
  getPublicStructure,
} from '../AppRoutes';
import routesConfig from '../AppRoutes/routeConfig';
import AuthWrapper from './AuthWrapper';
import { roles } from '@nxsys/store/constants';
import { IRELAND, PERMISSIONS } from '@nxsys/store/constants/keys';
import SuperAdminLayout from '@nxsys/components/AppLayout/SuperAdminLayout';
import { useConversationSocket } from '@nxsys/hooks/useConversationSocket';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import usePageTitle from '@nxsys/hooks/usePageTitle';

// Stripping these from the address bar with history.replaceState does not
// update the router's location, so a later navigate() would re-attach them from
// its stale copy and feed the same spent code back into the effect below. Every
// redirect that leaves a handoff has to drop them through the router too.
const HANDOFF_PARAMS = [
  'auth_code',
  'is_bureau',
  'super_admin_id',
  'super_admin_email',
  'super_admin_token',
  'super_admin_payroll_country',
];

const AppLayout = () => {
  const { navStyle } = useLayoutContext();
  const { user, isAuthenticated } = useAuthUser();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const isStandalonePage =
    location.pathname.startsWith('/create-company/') ||
    location.pathname === '/oauth/authorize' ||
    // Employee onboarding is a focused, blocking flow — no sidebar or navbar to
    // navigate away with.
    location.pathname === '/onboarding';
  const { updateNavStyle } = useLayoutActionsContext();
  const { updateMenuStyle, setSidebarBgImage } = useSidebarActionsContext();
  const AppLayout = Layouts[navStyle];
  const [params] = useUrlSearchParams();

  usePageTitle();
  const { isUpdateAvailable, reload } = useVersionCheck();

  const dispatch = useDispatch<AppDispatch>();
  const {
    authCode,
    isBureau,
    payrollCountry,
    isBureauEmployer,
    isAdmin,
    isEmployee,
    isAgency,
    permissions,
    user: userFromStore,
    token: sessionToken,
  } = useSelector((state: AppState) => {
    return state.appStore;
  });
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('auth');
  const authCodeClearedFromUrl = useRef(
    sessionStorage.getItem('AUTH_CODE_CLEARED') === 'true',
  );
  const appDataLoaded = useRef(false);
  const appDataRunning = useRef(false);

  const removeSplash = useCallback(() => {
    const splash = document.getElementById('initial-splash');
    if (splash) {
      splash.style.animation = 'splashFadeOut 0.3s ease-out forwards';
      setTimeout(() => splash.remove(), 300);
    }
  }, []);

  // Remove HTML splash when loading finishes (covers non-authenticated path)
  useEffect(() => {
    if (!isAppLoading) removeSplash();
  }, [isAppLoading, removeSplash]);

  const permissionMap = useMemo(
    () => buildPermissionMap(routesConfig(isBureau, payrollCountry) as any),
    [isBureau, payrollCountry],
  );

  /**
   * The screen a session timeout interrupted, if the user who has just signed
   * in may open it. The login screen has already established that the path is
   * one of ours; this decides whether this particular user may land there.
   *
   * Permissions are re-read from storage rather than taken from the selector:
   * this runs inside an effect that does not re-run once getPermissions
   * resolves, so the closure still holds the previous user's.
   */
  const resolveReturnPath = useCallback(
    (me: any): string | null => {
      const path = takeReturnPath();
      if (!path) return null;
      // An employee with onboarding outstanding is confined to /onboarding.
      if (me?.roles === 'subcontractor' && me?.isOnboarding) return null;
      try {
        const perms: string[] = JSON.parse(
          localStorage.getItem(PERMISSIONS) || '[]',
        );
        return checkRoutePermission(
          path.split('?')[0],
          me?.role,
          perms,
          permissionMap,
        )
          ? path
          : null;
      } catch {
        return null;
      }
    },
    [permissionMap],
  );

  // An employee with onboarding outstanding may only use /onboarding. Read from
  // the store rather than the auth user so it updates as soon as the last step
  // is saved, without needing a re-login.
  const lockToOnboarding = !!(isEmployee && userFromStore?.isOnboarding);

  const generatedRoutes = generateRoutes({
    isAuthenticated: isAuthenticated,
    userRole: user?.role,
    userPermissions: permissions,
    permissionMap,
    publicStructure: getPublicStructure(),
    authorizedStructure: authorizedStructure(
      isBureau,
      payrollCountry,
      lockToOnboarding,
    ),
    anonymousStructure: getAnonymousStructure(lockToOnboarding),
  });
  const routes = useRoutes(generatedRoutes);

  const handleLoginAs = (
    superAdminId: string,
    superAdminEmail: string,
    superAdminPayrollCountry: string,
    token: string,
  ) => {
    // Save super admin state for "back to super admin" feature
    const currentUser = {
      token,
      user: {
        id: superAdminId,
        email: superAdminEmail,
        username: superAdminEmail,
        roles: 'super_admin',
        payrollCountry: superAdminPayrollCountry,
      },
    };
    localStorage.setItem('ADMIN_LOGIN_AS', JSON.stringify(currentUser));
    dispatch(appActions.applyLoginAsState());
  };

  // Save ADMIN_LOGIN_AS immediately on mount (before any useEffect runs getAppData)
  // so getMe.fulfilled can read it and set isSuperAdmin
  if (
    params?.auth_code &&
    params?.super_admin_token &&
    params?.super_admin_id &&
    params?.super_admin_email
  ) {
    const existingAdmin = localStorage.getItem('ADMIN_LOGIN_AS');
    if (!existingAdmin || existingAdmin === '{}') {
      handleLoginAs(
        params.super_admin_id as string,
        params.super_admin_email as string,
        (params.super_admin_payroll_country as string) || '',
        params.super_admin_token as string,
      );
    }
  }

  useEffect(() => {
    if (params?.auth_code) {
      // An auth_code carries only a bare user id, and payroll and umbrella each
      // mint ids from their own USRP-<n> sequence, so the id alone is ambiguous.
      // Treating a missing flag as a default would resolve the id against
      // whichever database that default names and can open a stranger's account,
      // so the issuing system has to be stated explicitly (NX-5722).
      if (params.is_bureau !== 'true' && params.is_bureau !== 'false') {
        dispatch(appActions.setAuthCode(''));
        navigate('/signin', {
          replace: true,
          excludeParams: HANDOFF_PARAMS,
        });
        return;
      }

      // Save current authenticated state before switching to auth_code
      if (isAuthenticated) {
        dispatch(appActions.savePreviousAuthState());
      }
      dispatch(
        appActions.setHandoffAuth({
          authCode: params.auth_code as string,
          isBureau: params.is_bureau === 'true',
        }),
      );

      // Clear sensitive auth params from URL to prevent exposure in browser history/address bar
      authCodeClearedFromUrl.current = true;
      sessionStorage.setItem('AUTH_CODE_CLEARED', 'true');
      const url = new URL(window.location.href);
      HANDOFF_PARAMS.forEach((p) => url.searchParams.delete(p));
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } else if (authCode && !authCodeClearedFromUrl.current) {
      // URL no longer has auth_code (navigated away externally) → restore previous state
      dispatch(appActions.setAuthCode(''));
      dispatch(appActions.restorePreviousAuthState());
    }
  }, [params?.auth_code, params?.is_bureau]);

  useEffect(() => {
    const getAppData = async () => {
      if (appDataRunning.current) return;
      appDataRunning.current = true;
      setIsAppLoading(true);
      setLoadingStage('auth');
      const result = await dispatch(appActions.getMe());
      if (appActions.getMe.rejected.match(result)) {
        appDataRunning.current = false;
        dispatch(appActions.setAuthCode(''));
        navigate('/signin', {
          replace: true,
          excludeParams: HANDOFF_PARAMS,
        });
        setIsAppLoading(false);
        return;
      }
      setLoadingStage('config');
      await dispatch(appActions.getAppConfigs());
      setLoadingStage('permissions');
      await dispatch(appActions.getPermissions());
      setLoadingStage('ready');
      const user = result.payload?.data?.data;

      // After login only: return to the screen a session timeout interrupted,
      // otherwise redirect to first accessible route if default page has no permission
      const justLoggedIn = sessionStorage.getItem('JUST_LOGGED_IN');
      if (justLoggedIn) {
        sessionStorage.removeItem('JUST_LOGGED_IN');
        const returnPath = resolveReturnPath(user);
        const dynamicUrl = returnPath || getInitialUrl();
        // Landing on the default page is still a redirect worth making when the
        // spent returnUrl is sitting in the address bar, because nothing else
        // drops it and preserveQuery would carry it through the whole session.
        const hasSpentReturnUrl = new URLSearchParams(
          window.location.search,
        ).has(RETURN_URL_PARAM);
        if (dynamicUrl !== initialUrl || hasSpentReturnUrl) {
          navigate(dynamicUrl, {
            // A resumed path brings its own query string, and preserving the
            // login page's would append a second one after it.
            preserveQuery: !returnPath,
            excludeParams: [RETURN_URL_PARAM],
            replace: true,
          });
        }
      }

      // Fire non-blocking fetches
      dispatch(
        notificationActions.getNotification({
          role: user?.role,
          userId: user?.id,
          subcontractorId: user?.subcontractorId?.id,
          agencyId: user?.agencyId?.id,
        }),
      );
      dispatch(messageActions.fetchUnreadCount());
      // Brief pause so user sees "Almost ready..." before app renders
      await new Promise((r) => setTimeout(r, 400));
      appDataLoaded.current = true;
      appDataRunning.current = false;
      setIsAppLoading(false);
    };
    if (sessionStorage.getItem('REDIRECTING_OUT')) return;
    if (appDataLoaded.current && !isAuthenticated) {
      // Auth lost (e.g. logout) — reset
      appDataLoaded.current = false;
      setIsAppLoading(false);
      return;
    }
    if (appDataLoaded.current) return; // Skip re-run when authCode changes
    if (isAuthenticated || authCode) {
      getAppData();
    } else {
      setIsAppLoading(false);
    }
  }, [isAuthenticated, authCode]);

  // NX-5877: the notification socket authenticates at its handshake with what
  // the session's HTTP requests carry — a hand-off session's auth_code, or else
  // the session token — and is put in that session's rooms. It connects to the
  // session's own system, reconnects whenever the session changes — a renewal
  // (NX-5238), a new sign-in, a hand-off — and closes when the session ends.
  useEffect(() => {
    if (!authCode && !(isAuthenticated && sessionToken)) return;
    const { isBureau } = resolveAuthTarget();
    return connectNotificationSocket(notificationSocketOf(isBureau), isBureau);
  }, [isAuthenticated, sessionToken, authCode]);

  // NX-6269: the other sockets carry events of the session's own system too,
  // so they move to its host whenever the session changes — a sign-in, a
  // hand-off — and close when it ends. Their handshakes authenticate too, so
  // they follow who is signed in rather than the token: each handshake reads
  // the stored token, so socket.io's own reconnects present a renewed one, and
  // a renewal does not drop the job progress in flight. Who: a hand-off's
  // auth_code, or else the user on their system (ids are per database).
  const sessionIdentity = !isAuthenticated
    ? ''
    : authCode
      ? `code:${authCode}`
      : `${isBureau ? 'bureau' : 'umbrella'}:${userFromStore?.id}`;
  useEffect(() => {
    if (!sessionIdentity) return;
    setSessionSystem(resolveAuthTarget().isBureau);
    return () => setSessionSystem(null);
  }, [sessionIdentity]);

  // socket.io does not retry a handshake the server refused, and a refusal for
  // an expired token can arrive before its renewal is stored: each new token
  // connects those sockets again and leaves the connected ones alone.
  useEffect(() => {
    if (sessionIdentity) reconnectSessionSockets();
  }, [sessionToken]);

  useEffect(() => {
    if (!(authCode || isAuthenticated) || !userFromStore) return;
    // NX-6278: drop the previous user's listeners, or a country switch
    // keeps receiving the old tenant's events.
    return onListenSocket();
  }, [isAuthenticated, authCode, userFromStore]);

  // Global conversation socket for real-time unread badge updates
  useConversationSocket({
    userId: userFromStore?.id,
    agencyId: isAgency ? userFromStore?.agencyId?.id : undefined,
    companyParentId: userFromStore?.companyParentId,
    isAdmin,
  });

  const isSuperAdmin =
    userFromStore?.roles === 'super_admin' ||
    userFromStore?.role === roles.supper_admin;


  useEffect(() => {
    if (params.layout) updateNavStyle(params.layout as string);
    if (params.menuStyle) updateMenuStyle(params.menuStyle as string);
    if (params.sidebarImage) setSidebarBgImage(true);
  }, [
    params.layout,
    params.menuStyle,
    params.sidebarImage,
    updateNavStyle,
    updateMenuStyle,
    setSidebarBgImage,
  ]);

  const paymentModel =
    userFromStore?.subcontractorId?.setting?.paymentModel ||
    userFromStore?.subcontractorId?.paymentModel;

  const isCIS = isCISPaymentModel(paymentModel);

  const routesConfigByAppName = useMemo(() => {
    if (isSuperAdmin) {
      return [
        {
          id: 'manage-account',
          title: 'Manage Account',
          messageId: 'superAdmin.manageAccount',
          url: '/manage-account/admin',
          allowedRoles: [roles.supper_admin],
        },
      ];
    }
    const isIreland = payrollCountry === IRELAND;
    const filteredRoutes = filterMenuByRole(
      routesConfig(isBureau, payrollCountry) as any,
      {
        isAdmin,
        isEmployee,
        isBureauEmployer,
        isAgency,
      },
      permissions,
      isBureau,
      isIreland,
    );

    if (isCIS) {
      return filteredRoutes.map((route: any) =>
        route.id === 'payslip'
          ? { ...route, messageId: 'sidebar.app.selfBills' }
          : route,
      );
    }

    return filteredRoutes.filter((route: any) => route.id !== 'earning-statement');
  }, [
    isBureau,
    payrollCountry,
    isAdmin,
    isEmployee,
    isBureauEmployer,
    isAgency,
    permissions,
    isSuperAdmin,
    isCIS,
  ]);

  const onListenSocket = () => {
    // Track listeners so the effect cleanup can remove them.
    const listeners: [typeof socketNotification, string, (...args: any[]) => void][] = [];
    const listen = (
      socket: typeof socketNotification,
      event: string,
      handler: (...args: any[]) => void,
    ) => {
      socket.on(event, handler);
      listeners.push([socket, event, handler]);
    };

    // notification
    const user: any = userFromStore;
    const notificationChannel = notificationChannelOf(user);
    if (notificationChannel) {
      listen(socketNotification, notificationChannel, function (data: any) {
        const newNoti = data?.data;
        if (isEmpty(newNoti)) return;
        // notification timesheet approval
        if (
          newNoti?.isApprovalTimesheet &&
          newNoti.submitedBy !== user?.username
        ) {
          dispatch(notificationActions.onReceiveNewNotification(newNoti));
        }
        // notification others
        if (!newNoti?.isApprovalTimesheet) {
          dispatch(notificationActions.onReceiveNewNotification(newNoti));
        }
      });
    }

    // NX-5877: the bureau channel carries the tenant's admin feed (payroll
    // approvals across all of its clients), so only admins listen to it.
    // Employers and agencies used to fall through to it.
    if (hasTenantFeed(user?.role)) {
      listen(
        socketNotification,
        `bureau-new-notification-${user?.companyParentId}`,
        function (data) {
          const newNotification = data?.data;
          if (!isEmpty(newNotification)) {
            dispatch(
              notificationActions.onReceiveNewNotification(newNotification),
            );
          }
        },
      );
    }

    // Force logout when admin disables portal access
    listen(socketNotification, `force-logout-${user?.id}`, () => {
      dispatch(appActions.logout()).then(() => {
        navigate('/signin', { replace: true });
      });
    });

    // Agency upload notification badge
    if (user?.role === roles.admin || user?.role === roles.employer) {
      dispatch(importAction.getAgencyUploadSummary());
      listen(
        socketImportProcess,
        `agency-upload-notification-${user?.companyParentId}`,
        () => {
          dispatch(importAction.getAgencyUploadSummary());
        },
      );
    }

    // Prospect onboarding & document pending count badges
    if (user?.role === roles.admin || user?.role === roles.employer) {
      dispatch(prospectActions.getProspectPendingCount());
      listen(
        socketOnboarding,
        `new-onboarding-${user?.companyParentId}`,
        // The push carries no count: the badges are scoped to the companies
        // this user may see, so they are always fetched again.
        () => {
          dispatch(prospectActions.getProspectPendingCount());
        },
      );
    }

    return () => {
      listeners.forEach(([socket, event, handler]) => socket.off(event, handler));
    };
  };

  return isAppLoading ? (
    <AppFixedLoading stage={loadingStage} />
  ) : (
    <>
      {isUpdateAvailable && <AppVersionUpdate onUpdate={reload} />}
      <AppOnlineStatus />
      <AppEnvBanner />
      {/* NX-5764: a session that predates 2FA is asked to enrol here, since
          the login gate it was created before is the only place that asks. */}
      {isAuthenticated && <AppTwoFactorEnforcer />}
      {isStandalonePage ? (
        <AppContentView routes={routes} fullBleed />
      ) : isAuthenticated && isSuperAdmin ? (
        <SuperAdminLayout routes={routes} />
      ) : isAuthenticated ? (
        <AppLayout routes={routes} routesConfig={routesConfigByAppName} />
      ) : (
        <AuthWrapper>
          <AppContentView routes={routes} />
        </AuthWrapper>
      )}
    </>
  );
};

export default AppLayout;
